/**
 * The tenant of the session that is CURRENTLY authoritative in this tab,
 * derived from the tab's access token itself.
 *
 * Why the token and not `AuthContext.tenant`:
 *
 *  1. Reachability. `OfflineProvider` wraps `App`, and `AuthProvider` lives
 *     INSIDE `App` (src/main.tsx, src/App.tsx). The offline store and the
 *     sync queue run underneath the auth context and cannot read it.
 *  2. Correctness. The tenant that a request will actually execute under is
 *     the `tenantId` claim of the bearer token the request carries — not
 *     whatever React state happens to say. Deriving it from the token makes
 *     the two impossible to disagree: token refresh, the cabinet
 *     « switch-tenant » flow (which replaces ONLY the access token, see
 *     AuthContext.applySwitchedSession) and the 401 clear are all followed
 *     for free, with no wiring to forget.
 *
 * The API signs `{ userId, tenantId, email, permissions }` and guarantees
 * EXACTLY ONE `tenantId` per access token (packages/api
 * src/services/auth.service.ts, `signAccessToken`).
 *
 * Nothing here trusts the token: the signature is never checked client-side
 * and must not be. The claim is used only as a LOCAL cache/queue
 * discriminator — a forged token would isolate its holder from their own
 * caches, never grant them anyone else's data (the server re-verifies).
 */

// ─────────────────────────────────────────────────────────────
// The access token of THIS tab
// ─────────────────────────────────────────────────────────────

/**
 * Storage keys of the tab's access token and of its expiry. Declared here
 * rather than in `@/api/client` so that this module stays dependency-free
 * (importing the client would create a cycle: client → offline-store →
 * active-tenant).
 */
export const ACCESS_TOKEN_KEY = 'accessToken';
export const TOKEN_EXPIRY_KEY = 'tokenExpiresAt';

/**
 * Each tab holds its own access token, in sessionStorage (per tab, kept
 * across a reload), so a switch or a refresh in one tab never changes the
 * dossier of another. Only the refresh token is shared between tabs.
 *
 * The same keys in localStorage form a hand-off slot: a tab without a token
 * takes the one found there and empties the slot. The application never
 * writes it; Playwright storage states do, since they cannot carry
 * sessionStorage.
 */
const TAB_KEYS = [ACCESS_TOKEN_KEY, TOKEN_EXPIRY_KEY] as const;
type TabKey = (typeof TAB_KEYS)[number];

/** Stands in for sessionStorage when the browser refuses it. Per tab, like it. */
const tabFallback = new Map<TabKey, string>();

function readTab(key: TabKey): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return tabFallback.get(key) ?? null;
  }
}

function writeTab(key: TabKey, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    if (value === null) tabFallback.delete(key);
    else tabFallback.set(key, value);
  }
}

function takeHandOff(key: TabKey): string | null {
  try {
    const value = localStorage.getItem(key);
    if (value === null) return null;
    localStorage.removeItem(key);
    writeTab(key, value);
    return value;
  } catch {
    return null;
  }
}

/** The access token of this tab, or null when it has none. */
export function getAccessToken(): string | null {
  const token = readTab(ACCESS_TOKEN_KEY);
  if (token !== null) return token;
  // The token and its expiry leave the slot together.
  const handedOver = takeHandOff(ACCESS_TOKEN_KEY);
  if (handedOver !== null) takeHandOff(TOKEN_EXPIRY_KEY);
  return handedOver;
}

/** ISO expiry of this tab's access token, or null when unknown. */
export function getAccessTokenExpiry(): string | null {
  return readTab(TOKEN_EXPIRY_KEY) ?? takeHandOff(TOKEN_EXPIRY_KEY);
}

/** Stores this tab's access token; `expiresAt` is kept when not given. */
export function setTabAccessToken(token: string, expiresAt?: string): void {
  writeTab(ACCESS_TOKEN_KEY, token);
  if (expiresAt !== undefined) writeTab(TOKEN_EXPIRY_KEY, expiresAt);
}

/** Drops this tab's access token and expiry, and empties the hand-off slot. */
export function clearTabAccessToken(): void {
  for (const key of TAB_KEYS) {
    writeTab(key, null);
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage blocked: there is no slot to empty.
    }
  }
}

/** The `tenantId` and `userId` claims of a JWT, each null when unusable. */
interface SessionClaims {
  tenantId: string | null;
  userId: string | null;
}

function claimString(claims: object, name: 'tenantId' | 'userId'): string | null {
  const value = (claims as Record<string, unknown>)[name];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** Decode the session claims of a JWT. Never throws. */
function decodeSessionClaims(token: string): SessionClaims {
  const none: SessionClaims = { tenantId: null, userId: null };
  const payload = token.split('.')[1];
  if (!payload) return none;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const claims: unknown = JSON.parse(new TextDecoder().decode(bytes));
    if (typeof claims !== 'object' || claims === null) return none;
    return { tenantId: claimString(claims, 'tenantId'), userId: claimString(claims, 'userId') };
  } catch {
    // Malformed / truncated token — no claim can be asserted.
    return none;
  }
}

/** Decode the `tenantId` claim of a JWT. Never throws. */
export function decodeTenantId(token: string): string | null {
  return decodeSessionClaims(token).tenantId;
}

/** Decode the `userId` claim of a JWT. Never throws. */
export function decodeUserId(token: string): string | null {
  return decodeSessionClaims(token).userId;
}

/**
 * Memoisation keyed on the token STRING. This is a pure cache, not mutable
 * state: the value is a deterministic function of the key, and a key change
 * recomputes it. `getActiveTenantId()` is called on every React Query key
 * hash (see `@/lib/query-client`), i.e. many times per render — decoding
 * base64 + JSON each time would be needless work.
 */
let memo: { token: string; claims: SessionClaims } | null = null;

function activeClaims(): SessionClaims | null {
  const token = getAccessToken();
  if (!token) {
    memo = null;
    return null;
  }
  if (memo && memo.token === token) return memo.claims;
  const claims = decodeSessionClaims(token);
  memo = { token, claims };
  return claims;
}

/**
 * `tenantId` of this tab's session, or `null` when there is no session (or
 * the token carries no usable claim).
 */
export function getActiveTenantId(): string | null {
  return activeClaims()?.tenantId ?? null;
}

/**
 * `userId` of this tab's session, or `null` when there is no session (or
 * the token carries no usable claim). The API keeps the same `userId` when
 * a cabinet user switches dossier (firm/tenant-access.service.ts).
 */
export function getActiveUserId(): string | null {
  return activeClaims()?.userId ?? null;
}

// ─────────────────────────────────────────────────────────────
// The dossier THIS tab's screens show
// ─────────────────────────────────────────────────────────────

/**
 * The dossier the tab's screens were built for: set when this tab
 * establishes, switches or adopts a session. It differs from the token's
 * tenant only while a refresh has brought the token back to the home
 * dossier under screens of a client file, until the user chooses (see
 * `TenantMismatchOverlay`). It lives in memory, so a reloaded tab starts
 * without one and adopts whatever session it rehydrates.
 *
 * Module state is per tab by construction (one JS realm per tab).
 */
let tabTenantId: string | null = null;

export function getTabTenantId(): string | null {
  return tabTenantId;
}

export function setTabTenantId(tenantId: string | null): void {
  tabTenantId = tenantId;
}
